'use client'
/**
 * Author: Valeria Sinnati
 *  /\_/\
 * ( -.- )   Interruttore chiaro/scuro. Il chiaro è il default (tokens.css):
 *  > ^ <    qui si sceglie solo il dark, che si ricorda in `localStorage`.
 *
 * La scelta va applicata PRIMA del primo paint, altrimenti chi ha scelto il
 * dark vede un lampo bianco a ogni caricamento: per questo c'è
 * `SCRIPT_TEMA` (tema.ts), da mettere inline nell'<head> (index.html della console,
 * layout del client). Il componente poi legge l'attributo già impostato.
 *
 * È una preferenza di comodo e non un dato: se lo storage non c'è (finestra
 * privata, cookie bloccati) si resta sul chiaro senza errori.
 */
import { useEffect, useState } from 'react'
import { CHIAVE_TEMA, type Tema } from './tema'

function temaCorrente(): Tema {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

function applicaTema(tema: Tema) {
  const radice = document.documentElement
  if (tema === 'dark') radice.dataset.theme = 'dark'
  else delete radice.dataset.theme
  try {
    if (tema === 'dark') localStorage.setItem(CHIAVE_TEMA, 'dark')
    else localStorage.removeItem(CHIAVE_TEMA)
  } catch {
    // storage non disponibile: vale solo per questa pagina
  }
}

export interface ThemeToggleProps {
  /** Nome accessibile quando il clic porta al dark */
  etichettaScuro?: string
  /** Nome accessibile quando il clic porta al chiaro */
  etichettaChiaro?: string
  className?: string
}

const SOLE = [
  'M12 3v2',
  'M12 19v2',
  'M5 5l1.5 1.5',
  'M17.5 17.5L19 19',
  'M3 12h2',
  'M19 12h2',
  'M5 19l1.5-1.5',
  'M17.5 6.5L19 5',
]
const LUNA = 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z'

/**
 * Più istanze possono stare sulla stessa pagina (la console ne ha una nella
 * barra laterale e una nella testata mobile): ognuna osserva `data-theme`
 * sull'<html>, così un clic sull'una aggiorna icona ed etichetta dell'altra.
 *  /\____/\
 * > •    • <   #1
 */
export function ThemeToggle({
  etichettaScuro = 'Passa al tema scuro',
  etichettaChiaro = 'Passa al tema chiaro',
  className = '',
}: ThemeToggleProps) {
  // Sul server non c'è il documento: si parte dal chiaro e l'effetto allinea
  const [tema, setTema] = useState<Tema>('light')
  useEffect(() => {
    setTema(temaCorrente())
    const osservatore = new MutationObserver(() => setTema(temaCorrente()))
    osservatore.observe(document.documentElement, { attributeFilter: ['data-theme'] })
    return () => osservatore.disconnect()
  }, [])

  const scuro = tema === 'dark'
  return (
    <button
      type="button"
      aria-label={scuro ? etichettaChiaro : etichettaScuro}
      title={scuro ? etichettaChiaro : etichettaScuro}
      onClick={() => {
        const prossimo: Tema = scuro ? 'light' : 'dark'
        applicaTema(prossimo)
        setTema(prossimo)
      }}
      className={
        `grid size-11 flex-none place-items-center rounded-pill bg-soft text-foreground ` +
        `transition-colors hover:bg-panel-3 ${className}`
      }
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-[18px]"
        aria-hidden="true"
      >
        {scuro ? (
          <>
            <circle cx="12" cy="12" r="4" />
            {SOLE.map((d) => (
              <path key={d} d={d} />
            ))}
          </>
        ) : (
          <path d={LUNA} />
        )}
      </svg>
    </button>
  )
}
